import { forwardRef } from 'react';

import type { TextInputProps } from './types';
import { Label } from '../../general/Label';
import { cn } from '../../utils/cn';

export type { TextInputProps } from './types';

/**
 * A form text input component that combines a label, input field, and error message.
 *
 * @param id - ID of the input element. Used for htmlFor association.
 * @param label - Label text for the input.
 * @param layout - Layout direction: "horizontal" (default) or "vertical".
 * @param error - Error message to display below the input.
 * @param placeholder - Placeholder text for the input.
 * @param disabled - Whether the input is disabled.
 * @param className - Additional CSS classes for the container.
 * @param labelClassName - Additional CSS classes for the label.
 * @param inputClassName - Additional CSS classes for the input wrapper.
 * @param ...props - Additional HTML input attributes.
 *
 * @example
 * // Basic usage with horizontal layout
 * <TextInput label="Vorname" id="firstName" />
 *
 * @example
 * // Vertical layout
 * <TextInput label="Email" id="email" layout="vertical" />
 *
 * @example
 * // With error state
 * <TextInput label="Email" id="email" error="Dieses Feld enthält einen Fehler" />
 *
 * @example
 * // Disabled state
 * <TextInput label="Vorname" id="firstName" disabled />
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
    (
        { className, disabled, error, id, inputClassName, label, labelClassName, layout = 'horizontal', ...props },
        ref,
    ) => {
        const isHorizontal = layout === 'horizontal';
        const hasError = Boolean(error);

        return (
            <div
                className={cn(
                    // Native <input size=20> sets ~20ch min-width; min-w-0 lets flex honor fixed widths.
                    'flex min-w-0',
                    isHorizontal ? 'flex-row gap-2.5 items-start' : 'flex-col gap-2 items-start',
                    className,
                )}
            >
                {label && (
                    <Label
                        htmlFor={id}
                        className={cn(
                            isHorizontal ? 'w-[125px] shrink-0 justify-end h-8' : 'w-full px-0 py-0 justify-start',
                            labelClassName,
                        )}
                    >
                        {label}
                    </Label>
                )}
                <div className={cn('flex min-w-0 flex-col', isHorizontal ? 'flex-1' : 'w-full')}>
                    <div
                        className={cn(
                            'flex h-8 w-full min-w-0 items-center',
                            'px-3',
                            'bg-white border rounded',
                            hasError ? 'border-alert-error' : 'border-grey-dark',
                            'focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-focus focus-within:ring-offset-1',
                            inputClassName,
                            disabled && 'cursor-not-allowed bg-surface-light opacity-50',
                        )}
                    >
                        <input
                            ref={ref}
                            id={id}
                            disabled={disabled}
                            className={cn(
                                'h-full min-w-0 flex-1',
                                'border-none bg-transparent outline-none',
                                'text-sm text-midnight',
                                'placeholder:text-midnight-placeholder',
                                'disabled:cursor-not-allowed disabled:bg-surface-light',
                            )}
                            {...props}
                        />
                    </div>
                    {hasError && <span className="mt-1 text-[10px] leading-[1.46] text-alert-error">{error}</span>}
                </div>
            </div>
        );
    },
);

TextInput.displayName = 'TextInput';
